//
import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const throwCheckInActionError = async (res, fallbackMessage) => {
	let payload = null
	let responseText = ''
	const contentType = res.headers.get('content-type') || ''

	if (contentType.includes('application/json')) {
		payload = await res.json().catch(() => null)
	} else {
		responseText = await res.text().catch(() => '')
	}

	const serverMessage =
		payload && typeof payload.message === 'string' ? payload.message.trim() : ''
	const textMessage = responseText.trim()
	const message = serverMessage || textMessage || fallbackMessage
	const err = new Error(message)
	err.status = res.status
	err.code =
		payload && typeof payload === 'object' && 'code' in payload
			? payload.code
			: undefined
	err.details =
		payload && typeof payload === 'object' && 'details' in payload
			? payload.details
			: undefined
	throw err
}

export const getCheckInById = async (orgSlug, id) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins/${id}`, {
		method: 'GET',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
	})

	if (!res.ok) {
		throw new Error('Check-In not found')
	}

	return res.json()
}

export const getCheckInMessageHistory = async (orgSlug, id) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins/${id}/messages`, {
		method: 'GET',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
	})

	if (!res.ok) {
		throw new Error('Failed to fetch check-in message history')
	}

	return res.json()
}

export const searchCheckIns = async (experienceSlug, orgSlug, query) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/search?q=${encodeURIComponent(
			query
		)}`,
		{
			method: 'GET',
			headers: {
				...getAuthHeaders(),
			},
		}
	)

	if (!res.ok) {
		throw new Error('Search failed')
	}

	return res.json()
}

export const getExperienceCustomFields = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/custom-fields`,
		{
			headers: getAuthHeaders(),
		},
	)

	if (!res.ok) {
		throw new Error('Failed to fetch custom fields')
	}

	return res.json()
}

export const updateCheckIn = async (orgSlug, id, data) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins/${id}`, {
		method: 'PATCH',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})

	if (!res.ok) {
		await throwCheckInActionError(res, 'Update failed')
	}

	return res.json()
}

export const addCheckInNote = async (orgSlug, id, note) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins/${id}/notes`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ note }),
	})

	if (!res.ok) {
		await throwCheckInActionError(res, 'Failed to add note')
	}

	return res.json()
}

export const updateCheckInNote = async (orgSlug, id, noteId, note) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/${id}/notes/${encodeURIComponent(
			noteId,
		)}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ note }),
		},
	)

	if (!res.ok) {
		await throwCheckInActionError(res, 'Failed to update note')
	}

	return res.json()
}

export const deleteCheckInNote = async (orgSlug, id, noteId) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/${id}/notes/${encodeURIComponent(
			noteId,
		)}`,
		{
			method: 'DELETE',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
		},
	)

	if (!res.ok) {
		await throwCheckInActionError(res, 'Failed to delete note')
	}

	return res.json()
}

export const createCheckInOnSiteSaleCheckout = async (
	orgSlug,
	checkInId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/${checkInId}/on-site-sales/checkout`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)

	if (!res.ok) {
		let payload = null
		const contentType = res.headers.get('content-type') || ''
		if (contentType.includes('application/json')) {
			payload = await res.json().catch(() => null)
		}
		const message =
			payload && typeof payload.message === 'string'
				? payload.message
				: 'Failed to create checkout'
		const err = new Error(message)
		err.status = res.status
		err.code = payload?.code
		throw err
	}

	return res.json()
}

export const addCheckInOnSiteFreeItem = async (orgSlug, checkInId, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/${checkInId}/on-site-sales/free-item`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)

	if (!res.ok) {
		let payload = null
		const contentType = res.headers.get('content-type') || ''
		if (contentType.includes('application/json')) {
			payload = await res.json().catch(() => null)
		}
		const message =
			payload && typeof payload.message === 'string'
				? payload.message
				: 'Failed to add item'
		const err = new Error(message)
		err.status = res.status
		err.code = payload?.code
		throw err
	}

	return res.json()
}

export const addCheckInOnSiteManualPaidItem = async (
	orgSlug,
	checkInId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/${checkInId}/on-site-sales/manual-payment`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)

	if (!res.ok) {
		let payload = null
		const contentType = res.headers.get('content-type') || ''
		if (contentType.includes('application/json')) {
			payload = await res.json().catch(() => null)
		}
		const message =
			payload && typeof payload.message === 'string'
				? payload.message
				: 'Failed to add item'
		const err = new Error(message)
		err.status = res.status
		err.code = payload?.code
		throw err
	}

	return res.json()
}

export const getExperienceCheckIns = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}`,
		{
			headers: { ...getAuthHeaders() },
		}
	)
	if (!res.ok) throw new Error('Failed to fetch check-ins')
	return res.json()
}

export const getIncompleteCheckInsWithIssuedItems = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/incomplete-issued-items`,
		{ headers: getAuthHeaders() },
	)
	if (!res.ok) {
		await throwCheckInActionError(res, 'Failed to download incomplete check-ins')
	}
	return res.json()
}

export const getOrgCheckIns = async (orgSlug, params = {}) => {
	const query = new URLSearchParams()
	const search =
		typeof params.search === 'string'
			? params.search.trim()
			: typeof params.q === 'string'
				? params.q.trim()
				: ''
	const experience =
		typeof params.experience === 'string' ? params.experience.trim() : ''
	const checkInStatus =
		typeof params.checkInStatus === 'string'
			? params.checkInStatus.trim()
			: ''

	if (search) query.set('q', search)
	if (experience) query.set('experience', experience)
	if (checkInStatus && checkInStatus !== 'all') {
		query.set('checkInStatus', checkInStatus)
	}

	const suffix = query.toString() ? `?${query.toString()}` : ''
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins${suffix}`, {
		headers: { ...getAuthHeaders() },
	})
	if (!res.ok) throw new Error('Failed to fetch organization check-ins')
	return res.json()
}

export const getUserCheckInHistory = async (experienceSlug, orgSlug, userId) => {
	const query = new URLSearchParams()
	if (userId) query.set('userId', userId)
	const suffix = userId ? `?${query}` : ''
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/history${suffix}`,
		{
			method: 'GET',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
		}
	)

	if (!res.ok) {
		throw new Error('Failed to fetch check-in history')
	}

	return res.json()
}

export const uploadExperienceCheckIns = async (
	experienceSlug,
	orgSlug,
	checkIns
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/upload`,
		{
			method: 'POST',
			headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify(checkIns),
		}
	)

	if (!res.ok) {
		await throwCheckInActionError(res, 'Failed to upload check-ins')
	}
	return res.json()
}

export const createCheckIn = async (experienceSlug, orgSlug, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/checkins/experience/${experienceSlug}/upload`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify([data]),
		}
	)
	if (!res.ok) throw new Error('Failed to create check-in')
	return res.json()
}

export const deleteCheckIn = async (id, orgSlug) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/checkins/${id}`, {
		method: 'DELETE',
		headers: {
			...getAuthHeaders(),
		},
	})

	if (!res.ok) {
		let payload = null
		const contentType = res.headers.get('content-type') || ''
		if (contentType.includes('application/json')) {
			payload = await res.json().catch(() => null)
		}
		const message =
			payload && typeof payload.message === 'string'
				? payload.message
				: 'Delete failed'
		const err = new Error(message)
		err.status = res.status
		err.code = payload?.code
		throw err
	}

	return res.json()
}
